import * as React from 'react';
import { JourneyNavigationHandler, Routes } from '../../../routes';
import { useEffect, useState } from 'react';
import { useSystem } from '../../../hooks/useSystem';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { DeleteAppWarnWidget } from './DeleteAppWarnWidget';
import { observer } from 'mobx-react';
import { DeleteAppConfirmWidget } from './DeleteAppConfirmWidget';
import { LoaderWidget } from '../../../widgets/LoaderWidget';
import { AppModel } from '@journeyapps-platform/lib-journey-core';
import { useDialogStore } from '../../../hooks/useDialogService';
import { useAllowed } from '../../../hooks/useAllowed';
import { Region } from '../../../utils/environments';
import { AppModelAction } from '@journeyapps-platform/cardinal-catalog';

export enum DeleteAppStep {
  WARN = 'step1',
  CONFIRM = 'step2'
}

export interface DeleteAppPayload {
  loading?: boolean;
  step: DeleteAppStep;
}

export const DeleteAppPage: React.FC<{ handler: JourneyNavigationHandler<Routes.DELETE_APP> }> = observer((props) => {
  const system = useSystem();
  const dialogStore = useDialogStore();
  const orgStore = useOrgStore();
  const { params, navigate } = props.handler;
  const [payload, setPayload] = useState<DeleteAppPayload>({ loading: true, step: DeleteAppStep.WARN });
  const [tempApp] = useState<AppModel>(
    system.appsStore.generate({
      id: params.app_id,
      auth_scope_id: '',
      name: '',
      region: Region.US,
      org_id: params.org_id,
      features: []
    })
  );

  const isAllowedToDeleteApp = useAllowed({ action: AppModelAction.DELETE, resource: tempApp });

  const showErrorDialog = async () => {
    await dialogStore.errorDialog({
      subTitle: `Failed to delete app`,
      errorMessage: `Oh no, something went wrong and this app couldn't be deleted. Please try again in a few minutes.`
    });
  };

  useEffect(() => {
    (async () => {
      const org = await system.orgStore.resolve(params.org_id);
      await org.loadApps();
      const app = await org.getApp(params.app_id);
      await app?.loadDeployments();
      setPayload({
        ...payload,
        loading: false
      });
    })();
  }, [params.app_id]);

  const app = useOrgStore().org(params.org_id)?.app(params.app_id);

  if (!app) {
    return <LoaderWidget />;
  }

  if (payload.step == DeleteAppStep.WARN) {
    return (
      <DeleteAppWarnWidget
        payload={payload}
        next={() => {
          setPayload({ ...payload, step: DeleteAppStep.CONFIRM });
        }}
        app={app}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
      />
    );
  }

  if (payload.step == DeleteAppStep.CONFIRM) {
    return (
      <DeleteAppConfirmWidget
        payload={payload}
        app={app}
        next={async () => {
          if (!isAllowedToDeleteApp) {
            await dialogStore.errorDialog({
              title: 'Not Allowed',
              errorMessage: `You do not have permission to delete a developer. Ask your administrator to delete this developer, or to elevate your permissions.`
            });
            return;
          }
          try {
            const client = system.clientProvisioner.provisionAlphaClient();
            const res = await client.deleteApp({
              app_id: params.app_id,
              org_id: params.org_id
            });

            if (!res.success) {
              console.error('Delete app was unsuccessful, response:', res);
              await showErrorDialog();
            } else {
              orgStore.getOrg(params.org_id).then((o) => o.loadApps());
            }
          } catch (err) {
            console.error('Delete app was unsuccessful, error:', err);
            await showErrorDialog();
          } finally {
            navigate(Routes.MANAGE_APPS, params);
          }
        }}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
      />
    );
  }

  return null;
});
